import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { AlertTriangle, Lightbulb, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const CloudyWater = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Cloudy Water" 
        description="Diagnosing and clearing cloudy pool water"
      >
        <section className="space-y-8">
          {/* Overview */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-foreground mb-3">Understanding Cloudy Water</h2>
            <p className="text-muted-foreground">
              Cloudy water is one of the most common pool problems. It's not just a cosmetic issue — 
              it usually indicates a chemistry or circulation problem that needs attention. The key is 
              diagnosing the cause correctly before treating.
            </p>
          </div>

          {/* Common Causes */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Common Causes</h2>
            <div className="space-y-4">
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">1. Poor Filtration</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  The most common cause. Filter can't remove particles from the water.
                </p>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Dirty filter (check pressure gauge — is it high?)</li>
                  <li>• Insufficient run time</li>
                  <li>• Filter media worn out or damaged</li>
                  <li>• Pump not running at adequate speed</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">2. Low Chlorine</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  Chlorine below 1 ppm allows bacteria and algae to start multiplying.
                </p>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Salt cell not producing enough</li>
                  <li>• High chlorine demand (heavy use, hot weather)</li>
                  <li>• CYA too high (chlorine lock)</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">3. High pH / Alkalinity</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  High pH reduces chlorine effectiveness and causes calcium to precipitate out of solution.
                </p>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• pH above 7.8</li>
                  <li>• High calcium hardness</li>
                  <li>• Calcium carbonate precipitation (white haze)</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">4. Early Algae</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  Cloudiness can be the first sign of algae before green is visible.
                </p>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Slight green tint when looking edgewise</li>
                  <li>• Slippery surfaces</li>
                  <li>• Chlorine dropping rapidly</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">5. Environmental Factors</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  External factors can introduce particles and contaminants.
                </p>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Heavy rain (dilutes chemicals, adds contaminants)</li>
                  <li>• High bather load (pool party)</li>
                  <li>• Pollen, dust, construction debris</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Diagnostic Flowchart */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Diagnostic Flowchart</h2>
            <div className="bg-card border rounded-lg p-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">1</span>
                  <div>
                    <p className="font-medium text-foreground">Check chlorine level</p>
                    <p className="text-sm text-muted-foreground">Below 1 ppm? → Shock the pool</p>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <ArrowRight className="h-4 w-4 text-muted-foreground rotate-90" />
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">2</span>
                  <div>
                    <p className="font-medium text-foreground">Check filter pressure</p>
                    <p className="text-sm text-muted-foreground">8-10 PSI above baseline? → Clean filter</p>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <ArrowRight className="h-4 w-4 text-muted-foreground rotate-90" />
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">3</span>
                  <div>
                    <p className="font-medium text-foreground">Check pH and alkalinity</p>
                    <p className="text-sm text-muted-foreground">pH above 7.6? → Add acid to lower</p>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <ArrowRight className="h-4 w-4 text-muted-foreground rotate-90" />
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">4</span>
                  <div>
                    <p className="font-medium text-foreground">Check CYA level</p>
                    <p className="text-sm text-muted-foreground">Above 80 ppm? → Partial drain needed</p>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <ArrowRight className="h-4 w-4 text-muted-foreground rotate-90" />
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">5</span>
                  <div>
                    <p className="font-medium text-foreground">Check pump run time</p>
                    <p className="text-sm text-muted-foreground">Less than 8 hours? → Increase run time</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Treatment */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Standard Treatment</h2>
            <div className="bg-card border rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                For most cloudy water situations, this approach works:
              </p>
              <ol className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">1</span>
                  <span><strong className="text-foreground">Balance chemistry first</strong> — Adjust pH to 7.2-7.4 (chlorine works best here)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">2</span>
                  <span><strong className="text-foreground">Shock the pool</strong> — Bring chlorine to 10+ ppm</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">3</span>
                  <span><strong className="text-foreground">Run pump 24 hours</strong> — Continuous filtration is critical</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">4</span>
                  <span><strong className="text-foreground">Clean filter</strong> — After 24 hours, clean filter to remove captured particles</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">5</span>
                  <span><strong className="text-foreground">Consider clarifier</strong> — If still hazy, clarifier can help (see below)</span>
                </li>
              </ol>
            </div>
          </div>

          {/* Clarifier vs Flocculant */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Clarifier vs Flocculant</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Clarifier</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-1" />Clumps tiny particles so filter can catch them</li>
                  <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-1" />Takes 24-48 hours to work</li>
                  <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-1" />Pool remains swimmable</li>
                  <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-1" />Best for light to moderate cloudiness</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Flocculant</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-1" />Clumps particles and sinks them to floor</li>
                  <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-1" />Works in 8-12 hours</li>
                  <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-1" />Requires vacuuming to waste</li>
                  <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-1" />For severe cloudiness or time-sensitive clearing</li>
                </ul>
              </div>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Pro Tip:</strong> Don't over-use clarifier. More isn't better — 
                  excess clarifier can actually make water cloudier. Follow dosing instructions.
                </p>
              </div>
            </div>
          </div>

          {/* When to Escalate */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">When to Escalate</h2>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-6">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-foreground mb-2">Contact the Office If:</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• Cloudiness persists after 48 hours of treatment</li>
                    <li>• Pool is clearing but client is upset/concerned</li>
                    <li>• Cloudiness keeps recurring after clearing</li>
                    <li>• You suspect equipment issue (filter, pump)</li>
                    <li>• Pool is solid milky white (possible calcium issue)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Prevention */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Prevention</h2>
            <div className="bg-card border rounded-lg p-6">
              <ul className="space-y-2 text-muted-foreground">
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Maintain chlorine at 2-4 ppm consistently</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Keep pH at 7.2-7.6 (lower is better for chlorine)</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Clean filter when pressure rises 8-10 PSI</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Run pump 8-12 hours minimum daily</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Shock after heavy use or rain</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Watch CYA levels — don't let them climb above 50</li>
              </ul>
            </div>
          </div>

          {/* Related */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Related Topics</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/wiki/equipment/filters" className="block p-4 bg-card border rounded-lg hover:border-primary transition-colors">
                <span className="font-medium">Filter Care</span>
                <p className="text-sm text-muted-foreground mt-1">Cleaning and maintenance</p>
              </Link>
              <Link to="/wiki/fundamentals/chemistry" className="block p-4 bg-card border rounded-lg hover:border-primary transition-colors">
                <span className="font-medium">Water Chemistry</span>
                <p className="text-sm text-muted-foreground mt-1">Balancing for clarity</p>
              </Link>
            </div>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default CloudyWater;